<script lang="ts">
  import { getModalStore } from '@skeletonlabs/skeleton';
  import { browser } from '$app/environment';
  import { showToast } from '$lib/helpers/toastrHelper';
  import Button from '$lib/components/common/button/Button.svelte';

  const modalStore = getModalStore();

  const responseData = $modalStore[0].meta.responseData;
  const success = $modalStore[0].meta.success;
  const title = $modalStore[0].meta.title || 'Tass Invoice Post';

  let view: 'json' | 'curl' | 'powershell' = 'json';

  const fileBaseName = `tass-${responseData?.method ?? 'post'}-${responseData?.invoiceId ?? 'invoice'}`;

  // The base64 attachment can be megabytes long, so the preview shortens it. Copy and download use the full value.
  function getJsonPreview(): string {
    return responseData.json.replace(
      /("attachment_file":")([^"]*)"/,
      (_: string, start: string, value: string) => `${start}<base64, ${value.length} characters - use Copy or Download for the full value>"`
    );
  }

  function getCurlPreview(): string {
    return responseData.curl.replace(/token=([^']{80})[^']+'/, (_: string, start: string) => `token=${start}...<shortened, use Copy or Download>'`);
  }

  function getPowerShellPreview(): string {
    return responseData.powerShell.replace(/token = '([^']{80})[^']+'/, (_: string, start: string) => `token = '${start}...<shortened, use Copy or Download>'`);
  }

  $: preview = view === 'json' ? getJsonPreview() : view === 'curl' ? getCurlPreview() : getPowerShellPreview();

  function getContent(): string {
    return view === 'json' ? responseData.json : view === 'curl' ? responseData.curl : responseData.powerShell;
  }

  $: viewLabel = view === 'json' ? 'JSON' : view === 'curl' ? 'curl' : 'PowerShell';

  async function copyContent() {
    const data = success ? getContent() : JSON.stringify(responseData, null, 2);
    if (!browser) {
      showToast({ title: 'Copy', message: 'Copy is not available on the server.', type: 'info' });
      return;
    }

    try {
      await navigator.clipboard.writeText(data);
      showToast({ title: 'Copied', message: `${viewLabel} copied to clipboard`, type: 'success' });
    } catch (err) {
      console.error('Failed to copy response:', err);
      showToast({ title: 'Copy Failed', message: 'Unable to copy to clipboard. Use Download instead.', type: 'error' });
    }
  }

  function downloadContent() {
    if (!browser) return;

    const content = view === 'json' ? responseData.json : view === 'curl' ? `#!/bin/sh\n${responseData.curl}\n` : `${responseData.powerShell}\n`;
    const blob = new Blob([content], {
      type: view === 'json' ? 'application/json' : view === 'curl' ? 'text/x-shellscript' : 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${fileBaseName}.${view === 'json' ? 'json' : view === 'curl' ? 'sh' : 'ps1'}`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  function handleClose() {
    modalStore.close();
  }
</script>

<div class="card w-modal max-w-4xl space-y-4 p-6" data-testid="modal" role="dialog" aria-modal="true">
  <header class="flex items-center justify-between">
    <h3 class="text-lg font-bold">{title}</h3>
  </header>

  <section class="space-y-4">
    {#if success}
      <div class="rounded-lg border border-green-200 bg-green-50 p-4">
        <div class="mb-2 flex items-center">
          <div class="mr-2 h-4 w-4 rounded-full bg-green-500"></div>
          <span class="font-medium text-green-800">Tass Post Generated</span>
        </div>
        <div class="mt-2 space-y-1 text-sm text-green-700">
          <div>Invoice ID: {responseData.invoiceId} | Invoice Number: {responseData.invoiceNumber}</div>
          <div>Method: {responseData.method} | Attachment: {responseData.hasAttachment ? 'included' : 'none'}</div>
          <div class="break-all">Posts to: {responseData.url}</div>
        </div>
      </div>

      {#if responseData.warnings?.length}
        <div class="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <ul class="list-disc space-y-1 pl-5 text-sm text-yellow-800">
            {#each responseData.warnings as warning}
              <li>{warning}</li>
            {/each}
          </ul>
        </div>
      {/if}

      <div class="flex gap-2">
        <Button label="JSON" colour={view === 'json' ? 'primary' : 'secondary'} onClick={() => (view = 'json')} />
        <Button label="curl" colour={view === 'curl' ? 'primary' : 'secondary'} onClick={() => (view = 'curl')} />
        <Button label="PowerShell" colour={view === 'powershell' ? 'primary' : 'secondary'} onClick={() => (view = 'powershell')} />
      </div>

      {#if view === 'curl'}
        <p class="text-sm text-gray-600">Running this command posts the invoice to the school's Tass. The token is the JSON encrypted with the entity's Tass key.</p>
      {:else if view === 'powershell'}
        <p class="text-sm text-gray-600">
          Running this script posts the invoice to the school's Tass. Save it as a .ps1 file and run the file, as the token can be too long to paste. The token is the JSON
          encrypted with the entity's Tass key.
        </p>
      {/if}

      <div class="max-h-96 overflow-auto rounded-lg border bg-gray-50 p-4">
        <pre class="whitespace-pre-wrap break-all font-mono text-xs text-gray-800">{preview}</pre>
      </div>
    {:else}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4">
        <div class="mb-2 flex items-center">
          <div class="mr-2 h-4 w-4 rounded-full bg-red-500"></div>
          <span class="font-medium text-red-800">Tass Post Generation Failed</span>
        </div>
      </div>
      <div class="max-h-96 overflow-auto rounded-lg border bg-gray-50 p-4">
        <pre class="whitespace-pre-wrap text-xs text-gray-800">{JSON.stringify(responseData, null, 2)}</pre>
      </div>
    {/if}
  </section>

  <footer class="flex justify-end gap-2">
    {#if success}
      <Button label={`Copy ${viewLabel}`} colour="secondary" size="medium" onClick={copyContent} />
      <Button label={`Download ${viewLabel}`} colour="secondary" size="medium" onClick={downloadContent} />
    {/if}
    <Button label="Close" colour="primary" size="medium" onClick={handleClose} />
  </footer>
</div>
